import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Wand2 } from 'lucide-react';
import {
  generateWardrobe, generateBookcase, generateDresser, generateKitchenCabinet,
  generateDesk, generateBed, generateNightstand, generateWallShelves,
  COLORS, MATERIALS,
} from '@/lib/furnitureData';

const FIELDS = {
  wardrobe: {
    title: 'Параметрический шкаф',
    defaults: { width: 120, height: 200, depth: 60, doors: 2, shelves: 3, drawers: 0, rods: 1, color: 'light_oak', material: 'mdf' },
    dims: { width: [40, 300], height: [100, 280], depth: [30, 80] },
    counts: [
      { id: 'doors', label: 'Двери', min: 1, max: 4 },
      { id: 'shelves', label: 'Полки', min: 0, max: 8 },
      { id: 'drawers', label: 'Ящики', min: 0, max: 6 },
      { id: 'rods', label: 'Штанги', min: 0, max: 3 },
    ],
    run: generateWardrobe,
  },
  bookcase: {
    title: 'Параметрический стеллаж',
    defaults: { width: 110, height: 150, depth: 30, shelves: 0, cols: 3, rows: 4, hasBack: true, hasDoors: false, color: 'light_oak', material: 'shelving_ldsp_16' },
    dims: { width: [40, 240], height: [40, 280], depth: [20, 50] },
    counts: [
      { id: 'cols', label: 'Секции (колонки)', min: 1, max: 6 },
      { id: 'rows', label: 'Ряды (ячейки по высоте)', min: 1, max: 6 },
      { id: 'shelves', label: 'Доп. полки (если без сетки)', min: 0, max: 10 },
    ],
    bools: [{ id: 'hasBack', label: 'Задняя стенка' }, { id: 'hasDoors', label: 'Нижняя дверца' }],
    run: generateBookcase,
  },
  dresser: {
    title: 'Параметрический комод',
    defaults: { width: 100, height: 85, depth: 50, drawers: 4, color: 'white', material: 'mdf' },
    dims: { width: [40, 200], height: [50, 160], depth: [30, 60] },
    counts: [{ id: 'drawers', label: 'Ящики', min: 2, max: 8 }],
    run: generateDresser,
  },
  kitchen_cabinet: {
    title: 'Параметрический кухонный шкаф',
    defaults: { width: 60, height: 85, depth: 56, doors: 1, shelves: 2, hasDrawer: false, color: 'white', material: 'mdf' },
    dims: { width: [30, 120], height: [50, 220], depth: [30, 70] },
    counts: [{ id: 'doors', label: 'Фасады', min: 1, max: 3 }, { id: 'shelves', label: 'Полки', min: 0, max: 6 }],
    bools: [{ id: 'hasDrawer', label: 'С ящиком' }],
    run: generateKitchenCabinet,
  },
  table: {
    title: 'Параметрический стол',
    defaults: { width: 140, height: 75, depth: 70, drawers: 1, hasCrossbar: true, color: 'dark_oak', material: 'solid_wood' },
    dims: { width: [60, 240], height: [40, 110], depth: [40, 120] },
    counts: [{ id: 'drawers', label: 'Ящики', min: 0, max: 3 }],
    bools: [{ id: 'hasCrossbar', label: 'Царга' }],
    run: generateDesk,
  },
  bed: {
    title: 'Параметрическая кровать',
    defaults: { width: 160, height: 25, depth: 200, hasHeadboard: true, color: 'dark_oak', material: 'solid_wood' },
    dims: { width: [80, 220], height: [15, 40], depth: [180, 220] },
    bools: [{ id: 'hasHeadboard', label: 'Изголовье' }],
    run: generateBed,
  },
  nightstand: {
    title: 'Параметрическая тумба',
    defaults: { width: 50, height: 55, depth: 40, drawers: 2, color: 'light_oak', material: 'mdf' },
    dims: { width: [30, 100], height: [30, 100], depth: [25, 50] },
    counts: [{ id: 'drawers', label: 'Ящики', min: 1, max: 4 }],
    run: generateNightstand,
  },
  shelf: {
    title: 'Параметрические полки',
    defaults: { count: 3, shelfWidth: 80, spacing: 30, depth: 25, color: 'walnut', material: 'solid_wood' },
    dims: { shelfWidth: [30, 180], depth: [15, 40] },
    counts: [
      { id: 'count', label: 'Количество', min: 1, max: 8 },
      { id: 'spacing', label: 'Шаг (см)', min: 15, max: 60 },
    ],
    run: generateWallShelves,
  },
};

export default function ParametricDialog({ open, onClose, category, onGenerate }) {
  const cfg = FIELDS[category];
  const [params, setParams] = useState({});

  useEffect(() => {
    if (cfg) setParams(cfg.defaults);
  }, [category, cfg]);

  if (!cfg) return null;

  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="parametric-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-[#C25934]" />
            {cfg.title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
          {/* Dimensions */}
          <div className="space-y-3">
            {Object.entries(cfg.dims).map(([key, [min, max]]) => (
              <ParamSlider key={key} label={labelFor(key)} value={params[key]} min={min} max={max}
                onChange={(v) => set(key, v)} unit="см" />
            ))}
          </div>
          {/* Counts */}
          {(cfg.counts || []).length > 0 && (
            <div className="space-y-3 pt-2 border-t border-[#E0DDD8]">
              {cfg.counts.map(f => (
                <ParamSlider key={f.id} label={f.label} value={params[f.id]} min={f.min} max={f.max}
                  onChange={(v) => set(f.id, v)} unit="" />
              ))}
            </div>
          )}
          {/* Booleans */}
          {(cfg.bools || []).length > 0 && (
            <div className="space-y-2 pt-2 border-t border-[#E0DDD8]">
              {cfg.bools.map(f => (
                <div key={f.id} className="flex items-center gap-2">
                  <Checkbox id={`pd-${f.id}`} checked={!!params[f.id]}
                    onCheckedChange={(v) => set(f.id, !!v)} />
                  <Label htmlFor={`pd-${f.id}`} className="text-xs cursor-pointer">{f.label}</Label>
                </div>
              ))}
            </div>
          )}
          {/* Material + Color */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E0DDD8]">
            <div>
              <Label className="text-xs text-[#5C5C5C] mb-1 block">Материал</Label>
              <Select value={params.material} onValueChange={(v) => set('material', v)}>
                <SelectTrigger className="h-8 text-xs rounded-sm border-[#E0DDD8]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MATERIALS.map(m => <SelectItem key={m.id} value={m.id}>{m.nameRu}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#5C5C5C] mb-1 block">Цвет</Label>
              <Select value={params.color} onValueChange={(v) => set('color', v)}>
                <SelectTrigger className="h-8 text-xs rounded-sm border-[#E0DDD8]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLORS.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hex }} />
                        {c.nameRu}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="pd-cancel">Отмена</Button>
          <Button
            onClick={() => { onGenerate(cfg.run(params)); onClose(); }}
            className="bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm"
            data-testid="pd-generate"
          >
            <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Сгенерировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function labelFor(key) {
  const m = { width: 'Ширина', height: 'Высота', depth: 'Глубина', shelfWidth: 'Ширина полки' };
  return m[key] || key;
}

function ParamSlider({ label, value, min, max, unit, onChange }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#5C5C5C]">{label}</span>
        <div className="flex items-center gap-1">
          <Input type="number" value={value} min={min} max={max}
            onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
            className="mono-text w-16 h-6 text-xs text-right border-[#E0DDD8] rounded-sm px-1" />
          {unit && <span className="mono-text text-[10px] text-[#5C5C5C]">{unit}</span>}
        </div>
      </div>
      <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={min} max={max} step={1} />
    </div>
  );
}
